import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ButtonSpinner from '../../components/ui/ButtonSpinner';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('password');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || !user || !role) return;
    if (role === 'client') navigate('/client/dashboard', { replace: true });
    else if (role === 'advisor') navigate('/advisor/dashboard', { replace: true });
    else if (role === 'admin') navigate('/admin/dashboard', { replace: true });
  }, [authLoading, user, role, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
        <div
          className="w-10 h-10 border-2 border-brand-navy border-t-transparent rounded-full animate-spin"
          aria-hidden
        />
        <p className="text-sm text-slate-600">Loading...</p>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const signedIn = await login(email, password);
      if (signedIn.role === 'client') navigate('/client/dashboard');
      else if (signedIn.role === 'advisor') navigate('/advisor/dashboard');
      else if (signedIn.role === 'admin') navigate('/admin/dashboard');
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const autofill = (role) => {
    setEmail(`${role}@libr.com`);
    setPassword('password');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-3xl font-bold text-brand-navy">
          <SafeIcon icon={FiIcons.FiBookOpen} className="text-brand-gold" />
          Libr Legacy
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Sign in to your account
        </h2>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-slate-200">
          <div className="mb-6 p-4 bg-slate-50 rounded-md border border-slate-200 text-sm">
            <p className="font-semibold text-slate-700 mb-2">Demo Accounts:</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => autofill('client')} className="text-brand-navy hover:underline">Client</button> |
              <button type="button" onClick={() => autofill('advisor')} className="text-brand-navy hover:underline">Advisor</button> |
              <button type="button" onClick={() => autofill('admin')} className="text-brand-navy hover:underline">Admin</button>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiAlertCircle} /> {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Email address</label>
              <div className="mt-1">
                <input
                  type="email"
                  required
                  className="input-field"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="mt-1">
                <input
                  type="password"
                  required
                  className="input-field"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full btn-primary py-3">
              {loading ? (
                <>
                  <ButtonSpinner />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            <Link to="/signup" className="font-medium text-brand-navy hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};